import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';

import appRoutes from '@/router/appRoutes';
import chatRoutes from '@/router/chatRoutes';
import errorRoutes from '@/router/errorRoutes';
import labRoutes from '@/router/labRoutes';
import loginRoutes from '@/router/loginRoutes';
import selfServiceRoutes from '@/router/selfServiceRoutes';
import { useThemeStore } from '@/stores/theme';
import { checkAuth, checkLoginType, redirectToLogin } from '@/utils/authService';
import { isInCapacitorApp } from '@/utils/device';

const isMobile = import.meta.env.MODE === 'mobile';

const routes: RouteRecordRaw[] = [
  ...loginRoutes(), // 로그인
  ...chatRoutes(), // 통역
  ...selfServiceRoutes(), // 셀프서비스
  ...appRoutes(), // 앱
  ...labRoutes(), // 실험실
  ...errorRoutes(), // 에러
  {
    path: '/',
    name: 'index',
    redirect: (to) => {
      if (isMobile) {
        return { path: '/app/splash' };
      }

      if (to.query.code) {
        return { path: `/chat/entrance/${to.query.code}`, query: {} };
      }

      const hostname = window.location.hostname;
      if (hostname.startsWith('pnu-')) {
        const origin = window.location.origin.replace('pnu-', '');
        window.location.href = `${origin}/login?type=admin&partnerCode=pnu`;
        return;
      }

      window.location.href = 'https://www.sconchat.com/';
      return;
    },
  },
];

export const scrollPositions = new Map<string, number>();

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    const el = document.querySelector('[data-scroll-container]');

    if (savedPosition) {
      const saved = scrollPositions.get(to.fullPath);
      el?.scrollTo({ top: saved ?? 0 });
      return false;
    }

    el?.scrollTo({ top: 0 });
    return false;
  },
});

/**
 * 모바일 앱에서 뒤로 가기에 대한 transition 설정을 위한 코드
 */
const state = { isBack: false };
export const setIsBack = (value: boolean) => {
  state.isBack = value;
};

router.beforeEach(async (to, from, next) => {
  if (checkAuth(to) || checkLoginType(to)) {
    redirectToLogin(to);
    return;
  }

  const isMobileRoute = to.path.startsWith('/app/');
  const isCapacitor = isInCapacitorApp();

  if (isMobileRoute && state.isBack) {
    to.meta.transition = 'slide-right';
    state.isBack = false;
  }

  if (isCapacitor) {
    document.body.classList.toggle('in-capacitor', isMobileRoute);
  }

  next();
});

router.afterEach((route) => {
  const { meta: { theme } = {} } = route;
  const themeStore = useThemeStore();
  themeStore.updateTheme(theme);
});

export default router;
